import { GetStaticPaths, GetStaticProps } from 'next';
import { BreadcrumbJsonLd, NextSeo, HowToJsonLd } from 'next-seo';
import Link from 'next/link';

import getGuidesPaths, {
  destinationItems,
  extraDestinationContent,
  sourceItems,
} from '../../guides/config';

import CreateAccount from '../../guides/CreateAccount';
import Done from '../../guides/Done';
import { getH3Content } from '../../guides/helpers';

export const ROOT_PATH = '/guides';

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = getGuidesPaths();

  return {
    paths: paths.map((path) => ({ params: { slug: path } })),
    fallback: true,
  };
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params }) => {
  if (!params?.slug || Array.isArray(params.slug)) return { notFound: true };

  const slugMatch = params.slug.match(/how-to-embed-(.*?)-in-(.*?)$/);
  if (!slugMatch) return { notFound: true };

  const [, source, destination] = slugMatch;

  if (!source || !destination) return { notFound: true };

  return {
    props: {
      source,
      destination,
    },
  };
};

type PageProps = {
  source: string;
  destination: string;
};

const GuidePage = ({ source, destination }: PageProps) => {
  const sourceItem = sourceItems[source];
  const destinationItem = destinationItems[destination];

  if (!sourceItem || !destinationItem) return null;

  const ExtraContent = extraDestinationContent[destination];

  const title = `How to embed ${sourceItem.name} ${sourceItem.calledAs} in ${destinationItem.name} ${destinationItem.calledAs}`;
  const description = `In this guide, we cover how you can embed ${sourceItem.name} ${sourceItem.calledAs} in ${destinationItem.name} ${destinationItem.calledAs}.`;
  const slug = `how-to-embed-${source}-in-${destination}`;

  return (
    <>
      <NextSeo
        title={`${title} | Stackblocks`}
        description={description}
        canonical={`https://stackblocks.app${ROOT_PATH}/${slug}`}
        openGraph={{
          url: `https://stackblocks.app${ROOT_PATH}/${slug}`,
          title: `${title} | Stackblocks`,
          description: description,
          images: [
            {
              url: `https://stackblocks.app/stackblocks-opengraph.png`,
              width: 1200,
              height: 627,
              alt: 'Stackblocks screenshot',
            },
          ],
          site_name: 'Stackblocks Webiste',
        }}
        twitter={{
          handle: '@aravindballa',
          site: '@StackblocksApp',
          cardType: 'summary_large_image',
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Stackblocks',
            item: `https://stackblocks.app`,
          },
          {
            position: 2,
            name: 'Guides',
            item: `https://stackblocks.app${ROOT_PATH}`,
          },
          {
            position: 3,
            name: title,
            item: `https://stackblocks.app${ROOT_PATH}/${slug}`,
          },
        ]}
      />
      <HowToJsonLd
        name={title}
        step={[
          {
            name: getH3Content(<CreateAccount />),
            item: `https://stackblocks.app${ROOT_PATH}/${slug}`,
          },
          ...sourceItem.steps.map((Step) => {
            const h3 = getH3Content(<Step />);
            if (!h3) return null;
            const [slugPart, name] = h3;
            return {
              name,
              item: `https://stackblocks.app${ROOT_PATH}/${slug}#${slugPart}`,
            };
          }),
          ...destinationItem.steps.map((Step) => {
            const h3 = getH3Content(<Step />);
            if (!h3) return null;
            const [slugPart, name] = h3;
            return {
              name,
              item: `https://stackblocks.app${ROOT_PATH}/${slug}#${slugPart}`,
            };
          }),
        ]}
      />
      <div className="mx-auto max-w-[65ch] w-full">
        <Link href={ROOT_PATH} className="text-sm underline">
          ← Back to all guides
        </Link>
      </div>
      <div className="my-12 prose prose-lg mx-auto prose-headings:font-serif">
        <h1>{title}</h1>
        <p>
          In this guide, we cover how you can embed <a href={sourceItem.link}>{sourceItem.name}</a>{' '}
          {sourceItem.calledAs} in a <a href={destinationItem.link}>{destinationItem.name}</a>{' '}
          {destinationItem.calledAs}.
        </p>
        {ExtraContent && <ExtraContent />}
        <h2 className="sr-only">Steps</h2>
        <ol>
          <CreateAccount />
          {sourceItem.steps.map((Step, i) => (
            <Step key={`source-${i}`} />
          ))}
          {destinationItem.steps.map((Step, i) => (
            <Step key={`destination-${i}`} />
          ))}
          <Done />
        </ol>
        <hr />
        <h2>Checkout other similar guides</h2>
        <ul>
          {destinationItem.otherLinks?.map((dest) => {
            const destItem = destinationItems[dest];
            if (!destItem) return null;
            return (
              <li key={dest}>
                <Link href={`${ROOT_PATH}/how-to-embed-${source}-in-${dest}`} className="text-base">
                  How to embed {sourceItem.name} {sourceItem.calledAs}in a {destItem.name}{' '}
                  {destItem.calledAs}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

export default GuidePage;
